<template>
  <div class="image-carousel">
    <div class="container">
      <div class="row">
        <div ref="overlayContent" class="col-lg-5 overlay-content on-overlay position-relative">
          <h1 v-html="title" />

          <!-- New petition button -->
          <div v-if="showPetitionStartButton" class="new-petition-button">
            <a :href="newPetitionPath"
               class="btn btn-primary btn-lg btn-wide btn-start-petition btn-with-shadow">
              <div class="button-icon">
                <i class="icon-plus" />
              </div>
              <span class="button-text">{{ startCampaignButtonText }}</span>
            </a>
            <div v-if="homepageCampaignHelp" class="small-text mt-2 campaign-help text-center" v-html="homepageCampaignHelp" />
          </div>

          <!-- Homepage custom links -->
          <div v-if="customLinks.length" class="homepage-custom-links small-text text-center my-3 my-md-0">
            <a v-for="link in customLinks" :key="link.id" :href="link.url" class="unmarked-link ms-3 fw-bold" v-html="link.text" />
          </div>
        </div>

        <div class="col-lg-7 px-0 position-relative">
          <div ref="spotlight" class="spotlight">
            <div ref="splide" :class="['splide', {'no-arrows': hideArrows}]">
              <div class="splide__track">
                <ul class="splide__list">
                  <li v-for="story in stories" :key="story.id" ref="slides" class="splide__slide">
                    <component :is="story.link ? 'a' : 'div'" :href="story.link" class="story-item">
                      <div class="list-item-image">
                        <img :src="story.image_url" :alt="story.title">
                        <div class="list-item-desc-box">
                          <div class="list-item-desc">
                            <span class="list-item-title">{{ story.title }}</span>
                            <span class="list-item-why">{{ story.content }}</span>
                          </div>
                        </div>
                      </div>
                    </component>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@splidejs/splide/css'
import Splide from '@splidejs/splide'

export default {
  props: {
    customLinks: {
      type: Array,
      required: false,
      default: () => []
    },
    homepageCampaignHelp: {
      type: String,
      required: false
    },
    newPetitionPath: {
      type: String,
      required: false
    },
    showPetitionStartButton: {
      type: Boolean,
      default: false
    },
    startCampaignButtonText: {
      type: String,
      required: false
    },
    stories: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: true
    }
  },

  computed: {
    hideArrows() {
      return this.stories.length <= 1
    }
  },

  mounted() {
    if (this.stories.length > 0) {
      this.initializeSplide()
      this.setupResizeHandler()
    }
  },

  beforeUnmount() {
    if (this.splide) {
      this.splide.destroy()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  },

  methods: {
    initializeSplide() {
      // Don't use loop type if there is a single story to avoid scrolling the same story over and over
      const carouselType = this.stories.length > 1 ? 'loop' : 'slide'
      const direction = this.i18n.isRtl() ? 'rtl' : 'ltr'
      const options = {
        type: carouselType,
        direction: direction,
        perPage: 1,
        perMove: 1,
        pagination: false,
        arrows: true,
        autoplay: false,
        classes: {
          arrows: 'splide__arrows arrows',
          arrow : 'splide__arrow arrow',
          prev  : 'splide__arrow--prev arrow-prev',
          next  : 'splide__arrow--next arrow-next'
        }
      }

      this.splide = new Splide(this.$refs.splide, options)

      this.splide.on('move', (newIndex) => {
        this.showDescBoxForSpotlightSlide(newIndex)
      })
      this.showDescBoxForSpotlightSlide(0)

      this.splide.mount()
    },

    showDescBoxForSpotlightSlide(spotlightSlideIndex) {
      this.$refs.splide.querySelectorAll('.list-item-desc-box').forEach(box => {
        box.style.display = 'none'
      })
      this.$refs.slides[spotlightSlideIndex].querySelector('.list-item-desc-box').style.display = 'block'
    },

    setupResizeHandler() {
      this.adjustSplideItemsWidth()
      this.updateOverlayPositions()

      // Set up resize handler
      this.resizeHandler = () => {
        this.adjustSplideItemsWidth()
        this.updateOverlayPositions()
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    // Adjust Splide slide widths based on spotlight size
    adjustSplideItemsWidth() {
      const spotlightWidth = this.$refs.spotlight.offsetWidth
      const slides = this.$el.querySelectorAll('.splide__slide')
      slides.forEach(slide => {
        slide.style.width = `${spotlightWidth}px`
      })

      // Refresh Splide to apply new dimensions if carousel is already initialized
      if (this.splide) {
        this.splide.refresh()
      }
    },

    // Update CSS custom properties for overlay positioning
    updateOverlayPositions() {
      const spotlight = this.$refs.spotlight
      const overlayContent = this.$refs.overlayContent

      if (!spotlight || !overlayContent) return

      const spotlightRect = spotlight.getBoundingClientRect()
      const containerRect = this.$el.getBoundingClientRect()

      // Calculate positions relative to the carousel container
      let spotlightStart = 0
      if (this.i18n.isRtl()) {
        spotlightStart = containerRect.right - spotlightRect.right
      } else {
        spotlightStart = spotlightRect.left - containerRect.left
      }
      const spotlightWidth = spotlightRect.width
      const containerWidth = containerRect.width
      const rightOverlayStart = spotlightStart + spotlightWidth

      // Set CSS custom properties that the pseudo-elements will use
      this.$el.style.setProperty('--spotlight-left', `${spotlightStart}px`)
      this.$el.style.setProperty('--spotlight-width', `${spotlightWidth}px`)
      this.$el.style.setProperty('--right-overlay-left', `${rightOverlayStart}px`)
      this.$el.style.setProperty('--right-overlay-width', `${containerWidth - rightOverlayStart}px`)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;
@use "~mixins/rtl_only" as rtl_override;

$carousel-item-width: 590px;
$carousel-max-width-without-padding: $carousel-item-width - 16px;

.image-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;

  // Create overlays with pseudo-elements using CSS custom properties
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    background: var(--carousel-background, url("../images/bg-stripe-black.png"));
    opacity: var(--carousel-background-opacity, 0.8);
    z-index: 1;
  }

  // Left overlay - covers from left edge to spotlight start
  &::before {
    left: 0;
    width: var(--spotlight-left, 0); // Use CSS custom property, fallback to 0

    @include breakpoints.screen-mobile-only {
      display: none; // Hide overlays on mobile
    }
  }

  // Right overlay - covers from spotlight end to right edge
  &::after {
    left: var(--right-overlay-left, 100%); // Use CSS custom property
    width: var(--right-overlay-width, 0); // Use CSS custom property

    @include breakpoints.screen-mobile-only {
      display: none; // Hide overlays on mobile
    }
  }

  // In RTL, swap the overlay sides so the visual layout mirrors correctly
  @include rtl_override.rtl-only {
    &::before {
      left: auto;
      right: 0;
      width: var(--right-overlay-width, 100%);
    }

    &::after {
      right: auto;
      left: 0;
      width: var(--spotlight-left, 0);
    }
  }

  h1, .new-petition-button {
    margin-top: 20px;
  }

  // Set the background color for the start petition button based on the CSS custom property
  // defined in sprockets. This allows us to customize the color from themes and also agra_private.
  .btn-start-petition {
    background: var(--carousel-accent-color);
  }

  @include breakpoints.screen-md-up {
    // The overlay content is only displayed on top of the carousel on desktop
    .overlay-content.on-overlay {
      min-height: 320px;
      z-index: 2;
      text-align: right;
      position: relative; // Ensure content appears above overlays

      h1 {
        font-size: 40px;
        line-height: 43px;
        text-align: right;
        margin-top: 50px;
      }

      .homepage-custom-links {
        position: absolute;
        bottom: 10px;
        right: 15px;
      }

      color: #fff;

      h1, .homepage-custom-links, .campaign-help {
        color: #fff;
      }

      .new-petition-button {
        position: absolute;
        bottom: 90px;
        right: 15px;
      }
    }
  }

  .spotlight {
    margin: auto;
    position: relative; // Ensure content appears above overlays
    z-index: 0;
  }

  // Splide container styling
  :deep(.splide) {
    width: 100%;
    overflow: visible;
    height: 525px;
  }

  // Splide arrows styling
  :deep(.splide__arrow) {
    background-color: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    position: absolute;
    top: 74%;
    cursor: pointer;
    font-size: 40px !important;
    color: #898989 !important;
    padding: 0 !important;
    margin: 0 !important;
    width: auto !important;
    height: auto !important;
    transform: translateY(-50%);
    z-index: 3; // Above overlays

    &:hover,
    &:active {
      color: #fff !important;
      text-decoration: none;
      border: none !important;
    }

    svg {
      width: 40px;
      height: 40px;
      fill: var(--carousel-control-color, currentcolor);

      &:active,
      &:hover {
        fill: var(--carousel-control-hover-color, currentcolor);
      }
    }
  }

  :deep(.splide.no-arrows .splide__arrows) {
    display: none;
  }

  :deep(.splide__arrow--prev) {
    left: 20px;

    @media screen and (max-width: #{$carousel-max-width-without-padding}) {
      left: 0;
    }
  }

  :deep(.splide__arrow--next) {
    right: 20px;

    @media screen and (max-width: #{$carousel-max-width-without-padding}) {
      right: 0;
    }
  }

  :deep(.splide__track) {
    overflow: visible;
  }

  :deep(.splide__list) {
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  :deep(.splide__slide) {
    position: relative;
    width: $carousel-item-width;
    height: 525px;
    line-height: 0;
  }

  .list-item-image {
    position: absolute;
    margin: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;

    img {
      min-width: 100%;
      min-height: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  .list-item-desc-box {
    position: absolute;
    top: 350px;
    width: 100%;
    height: 80px;
    background-color: var(--carousel-list-item-desc-box-background-color, #000);
    opacity: var(--carousel-list-item-desc-box-background-opacity, 0.8);

    @include breakpoints.screen-mobile-only {
      height: 160px;
    }

    @include breakpoints.screen-md-only {
      height: 100px;
    }
  }

  .list-item-desc {
    margin: 10px 70px 0;
    height: 60px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;

    @include breakpoints.screen-mobile-only {
      margin-left: 50px;
      margin-right: 50px;
      height: 140px;
    }

    @include breakpoints.screen-md-only {
      height: 80px;
    }

    .list-item-title {
      color: var(--carousel-accent-color);
      font-weight: bold;
      overflow-wrap: break-word;
      word-break: normal;
    }

    .list-item-why {
      color: var(--carousel-why-color, #fff);
      overflow-wrap: break-word;
      word-break: normal;
    }
  }

  // Hide Splide pagination since original design doesn't show it
  :deep(.splide__pagination) {
    display: none;
  }
}

.story-item {
  display: block;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: none;
    color: inherit;
  }
}
</style>
